import type { LocationQuery, LocationQueryValue } from 'vue-router'

import type { DiagnosticPayload } from '@backmarket/http-api/src/api-models/BuybackOffer'
import { camelizeKeys } from '@backmarket/utils/object/camelizeKeys'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { spacesToHyphens } from '@backmarket/utils/string/spacesToHyphens'
import type { Step } from '@ds/components/StatusTracker'

import type {
  BuybackModelInstruction,
  BuybackModelTimeline,
} from '@backmarket/nuxt-layer-buyback/types'

function toCheckupValue(
  value: number | undefined | LocationQueryValue | LocationQueryValue[],
): number | null {
  return value && !Array.isArray(value) ? Number(value) : null
}

export function toNewBuybackOrderCheckup(
  diagnosticPayload: DiagnosticPayload | LocationQuery,
) {
  const camelCasedPayload = camelizeKeys(diagnosticPayload)

  return {
    estheticCondition: toCheckupValue(camelCasedPayload.estheticCondition),
    screenCondition: toCheckupValue(camelCasedPayload.stateScreen),
    casingCondition: toCheckupValue(camelCasedPayload.stateBody),
    workingCondition: toCheckupValue(camelCasedPayload.stateFunctional),
    batteryCondition: toCheckupValue(camelCasedPayload.battery),
  }
}

export const NO_DATA_STRING = '-'

export type FormattedStatusTimeline = Step & {
  current: boolean
  tag: string
  hint: string | null
  date: Date | null
  details: string | null
}

export interface InfoBlockData {
  content: string
  icon: string
  title: string
  variant: string
}

export const formatStatusTimeline = (
  statusTimeline: readonly BuybackModelTimeline[],
): FormattedStatusTimeline[] => {
  return statusTimeline.map((step) => ({
    current: step.current,
    tag: step.tag,
    hint: step.hint || '',
    label: step.label,
    key: spacesToHyphens(step.label.toLowerCase()),
    date: step.date ? new Date(step.date) : null,
    details: step.info,
  }))
}

export const getStatusTimelineActiveStep = (
  statusTimeline: FormattedStatusTimeline[],
) => {
  const { tag, key: activeStep } =
    statusTimeline.find((status) => status.current) || statusTimeline[0]

  return { tag, activeStep }
}

export const getInfoData = (
  instruction: BuybackModelInstruction | undefined,
): InfoBlockData | null => {
  if (isEmpty(instruction)) {
    return null
  }
  const { body, icon, title: bannerTitle } = instruction

  return {
    content: body,
    icon,
    title: bannerTitle,
    variant: 'info',
  }
}
